<template>
  <div class="medical-analyses-list">
    <medical-analyses-sidesheet ref="modal" @delete-type="onRemoveGroup" @update="reload" />
    <u-confirm id="delete-doctype" ref="confirm" />
    <c-loader v-if="loading" label="" :active="loading" height="500" />

    <div v-if="!loading">
      <div v-if="hasVisibleItems">
        <draggable
          v-model="analyseGroups"
          tag="div"
          ghost-class="sortable__ghost"
          chosen-class="sortable-chosen"
          class="item-container"
          group="list"
          :force-fallback="true"
          @change="resortAnalyses"
        >
          <div v-for="group in analyseGroups" :key="group.id" class="item-group" data-type="group">
            <medical-analyses-group
              v-show="isAnalyseVisible(group)"
              :group="group"
              :query="normalizedQuery"
              @remove-analyse="onAnalyseRemove"
              @edit-group="onEditGroup"
              @remove-group="onRemoveGroup"
              @resort="resortAnalyses"
              @add-analyse="onAddingAnalyse"
            />
          </div>
        </draggable>
      </div>
      <div v-else class="documents-list__empty">Ничего не найдено. Попробуйте изменить параметры поиска.</div>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";

import MedicalAnalysesSidesheet from "./medical-analyses-sidesheet";
import Confirm from "@/pages/documents/u-confirm";
import Loader from "@/components/loader";
import MedicalAnalysesGroup from "./medical-analyses-group";

export default {
  name: "medical-analyses-list",
  components: {
    draggable,
    MedicalAnalysesSidesheet,
    MedicalAnalysesGroup,
    "u-confirm": Confirm,
    "c-loader": Loader,
  },
  props: {
    query: {
      type: String,
      default: () => "",
    },
  },
  data() {
    return {
      analyseGroups: [],
      loading: true,
      hasVisibleItems: true,
    };
  },
  computed: {
    normalizedQuery() {
      return this.query.trim().toLowerCase();
    },
  },
  watch: {
    query() {
      this.hasVisibleItems = this.analyseGroups.some((d) => {
        if (this.isAnalyseVisible(d)) return true;

        return d.analyses.some((analyse) => this.isAnalyseVisible(analyse));
      });
    },
  },
  created() {
    this.loadAnalyses();
  },
  methods: {
    addAnalyseType() {
      this.$refs.modal.open(null);
    },
    loadAnalyses() {
      this.loading = true;
      this.$store.dispatch("medicalAnalyses/fetchAllAnalyzes").then((analyses) => {
        this.analyseGroups = analyses;
        this.loading = false;
      });
    },
    resortAnalyses() {
      const sortingData = [];
      const listLength = this.analyseGroups.length;

      for (let i = 0; i < listLength; i++) {
        const groupAnalyseLength = this.analyseGroups[i].analyses.length;
        for (let j = 0; j < groupAnalyseLength; j++) {
          sortingData.push({
            id: this.analyseGroups[i].analyses[j].id,
            type: "analysis",
          });
        }
        sortingData.push({
          id: this.analyseGroups[i].id,
          type: "type",
        });
      }

      this.$store.dispatch("medicalAnalyses/resort", {
        list: sortingData.map((item, itemIndex) => ({
          ...item,
          sort: sortingData.length - itemIndex,
        })),
      });
    },
    onAnalyseRemove({ analyse, group }) {
      this.$refs.confirm.open(
        {
          title: "Удаление анализа",
          text: `Анализ <b>${analyse.name}</b> будет удалён без возможности восстановления`,
        },
        () => {
          this.$store.dispatch("medicalAnalyses/deleteAnalyse", analyse.id)
            .then((res) => {
              if (res.status == 400) {
                this.$notify({
                  group: "error-message",
                  data: {
                    textMessage: "Невозможно удалить медицинский анализ, который используется хотя бы у одного сотрудника."
                  },
                });
              } else if (res.status == 200) {
                this.$notify({
                  group: "success-message",
                  data: {
                    textMessage: `Анализ <b>${analyse.name}</b> удалён`,
                  },
                });
                group.analyses = group.analyses.filter((d) => d !== analyse)
              } else {
                this.$notify({
                  group: "error-message",
                  data: {
                    textMessage: "Произошла ошибка при удалении анализа"
                  },
                });
              }
            });
        },
      );
    },
    onEditGroup({ group }) {
      this.$refs.modal.open({ analyseType: group });
    },
    onRemoveGroup(group, fromSideSheet = false) {
      this.$refs.confirm.open(
        {
          title: "Удаление типа анализа",
          text: `Тип анализа <b>${group.name}</b> будет удалён без возможности восстановления`,
        },
        () => {
          this.$store.dispatch("medicalAnalyses/deleteAnalyseType", group.id).then(() => {
            this.analyseGroups = this.analyseGroups.filter((d) => d !== group);
            if (fromSideSheet) {
              this.$refs.modal.close();
            }
          });
        },
      );
    },
    onAddingAnalyse({ typeId, analyse }) {
      this.$store.dispatch("medicalAnalysesForm/changeAnalyseType", {
        id: analyse.id,
        typeId,
      });
    },
    reload() {
      this.loadAnalyses();
    },
    isAnalyseVisible(analyse) {
      if (!this.normalizedQuery) return true;
      if (analyse.name.toLowerCase().includes(this.normalizedQuery)) return true;

      if (analyse.description !== undefined && analyse.description.toLowerCase().includes(this.normalizedQuery)) {
        return true;
      }

      if ("analyses" in analyse) {
        return analyse.analyses.some((analyse) => this.isAnalyseVisible(analyse));
      }

      return false;
    },
  },
};
</script>

<style lang="scss" scoped>
.medical-analyses-list {
  max-width: 1160px;
}
.documents-list__empty {
  @media (max-width: 480px) {
    padding: 0 20px;
  }
}
</style>

<style lang="scss">
@import "~@/assets/styles/vars";

.sortable__ghost {
  height: 5px;
  overflow: hidden;
  text-overflow: ellipsis;
  background: $color-primary;
  opacity: 1;
  width: inherit !important;

  * {
    display: none;
  }
}
.sortable-drag {
  height: fit-content !important;
  z-index: 10 !important;
  opacity: 1 !important;
  background: white !important;
  border-bottom: none !important;
  box-shadow: 0px 3px 16px rgba(0, 102, 255, 0.7) !important;

  *:not(.toggle-button) {
    border: none;
  }
}
</style>
